import { useQueryClient } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import {
  isApiClientError,
  isApiResponseValidationError,
  isApiTransportError,
} from "../api/errors";
import { InstallButton } from "../pwa";
import { LoginLogoMark } from "./BrandLogo";
import { shellBootstrapKey } from "./bootstrap";
import { LabeledInput } from "./LabeledInput";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";

function returnPathFromSearch(search: unknown): string {
  const value =
    typeof search === "object" && search !== null && "redirect" in search
      ? search.redirect
      : undefined;
  return typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
    ? value
    : "/";
}

function loginFailureMessage(error: unknown, needsTwoFactor: boolean): string {
  if (isApiClientError(error) && error.status === 422) {
    return needsTwoFactor
      ? "Код подтверждения не принят. Проверьте код и время на устройстве."
      : "Не удалось войти: email или пароль не совпадают с данными текущей среды. Открытая сессия в другом браузере не подтверждает, что сохранённый пароль актуален.";
  }

  if (isApiClientError(error) && error.status === 403) {
    return "Доступ к учётной записи ограничен. Обратитесь к администратору.";
  }

  if (isApiClientError(error) && error.status === 429) {
    const retryAfter = error.responseHeaders.retryAfter;
    return retryAfter === null
      ? "Слишком много попыток входа. Подождите немного и попробуйте снова."
      : `Слишком много попыток входа. Повторите через ${retryAfter} сек.`;
  }

  if (isApiClientError(error) && error.status === 419) {
    return "Сеанс формы входа истёк. Обновите страницу и повторите попытку.";
  }

  if (isApiClientError(error) && error.status >= 500) {
    const requestReference = error.requestId
      ? ` Код обращения: ${error.requestId}.`
      : "";
    return `Сервис входа временно недоступен. Повторите позже.${requestReference}`;
  }

  if (isApiTransportError(error)) {
    return "Не удалось связаться с сервером. Проверьте подключение и повторите попытку.";
  }

  if (isApiResponseValidationError(error)) {
    return "Сервер вернул неожиданный ответ. Обновите страницу и повторите попытку.";
  }

  return needsTwoFactor
    ? "Не удалось проверить код подтверждения. Повторите позже или обратитесь к администратору."
    : "Не удалось выполнить вход. Повторите позже или обратитесь к администратору.";
}

export function LoginPage({ services }: { services: ShellServices }) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const search: unknown = router.state.location.search;
  const expired =
    typeof search === "object" &&
    search !== null &&
    "expired" in search &&
    search.expired === true;

  const finishLogin = async (twoFactorSetupRequired = false) => {
    await queryClient.invalidateQueries({ queryKey: shellBootstrapKey });
    await router.navigate({
      to: twoFactorSetupRequired
        ? "/account/security"
        : returnPathFromSearch(router.state.location.search as unknown),
    });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setMessage(null);

    try {
      if (needsTwoFactor) {
        const response = await services.identity.challengeTwoFactor(
          useRecoveryCode
            ? { recovery_code: code.trim() }
            : { code: code.trim() },
        );
        await finishLogin(response.data.user.two_factor.setup_required);
        return;
      }

      const response = await services.identity.login(email.trim(), password);
      if ("two_factor" in response) {
        setNeedsTwoFactor(true);
        setCode("");
        return;
      }
      await finishLogin(response.data.user.two_factor.setup_required);
    } catch (error) {
      if (
        !needsTwoFactor &&
        isApiClientError(error) &&
        error.status === 404 &&
        error.operationId === "postApiV1AuthLogin"
      ) {
        try {
          const currentUser = await services.identity.currentUser();
          await finishLogin(currentUser.two_factor.setup_required);
          return;
        } catch {
          // The original login failure is more useful than the recovery error.
        }
      }

      setMessage(loginFailureMessage(error, needsTwoFactor));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-brand" aria-label="Алтайэкопарк ERP">
        <LoginLogoMark />
        <p className="eyebrow eyebrow--light">Единое рабочее пространство</p>
        <h1>Алтайэкопарк ERP</h1>
        <p>
          Документы, задачи и корпоративные сервисы — в защищённом интерфейсе
          для сотрудников.
        </p>
      </section>

      <section className="login-panel" aria-labelledby="login-title">
        <form className="login-form" onSubmit={(event) => void submit(event)}>
          <p className="eyebrow">Рабочий аккаунт</p>
          <h2 id="login-title">
            {needsTwoFactor ? "Подтвердите вход" : "Вход в систему"}
          </h2>
          <p className="form-intro">
            {needsTwoFactor
              ? useRecoveryCode
                ? "Введите один из сохранённых recovery-кодов."
                : "Введите код из приложения-аутентификатора."
              : "Используйте выданные администратором данные."}
          </p>

          {needsTwoFactor ? (
            <label className="field">
              <span>
                {useRecoveryCode ? "Recovery-код" : "Код подтверждения"}
              </span>
              <input
                autoComplete="one-time-code"
                inputMode={useRecoveryCode ? "text" : "numeric"}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                autoFocus
              />
            </label>
          ) : (
            <>
              <LabeledInput
                label="Email"
                type="email"
                autoComplete="username"
                placeholder="name@company.ru"
                required
                autoFocus
                value={email}
                onChange={setEmail}
              />
              <LabeledInput
                label="Пароль"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={setPassword}
              />
            </>
          )}

          {message === null ? null : <Notice>{message}</Notice>}
          {expired && message === null ? (
            <Notice>Сессия завершена. Войдите снова.</Notice>
          ) : null}

          <button
            className="button button--primary button--wide"
            disabled={pending}
            type="submit"
          >
            {pending ? "Проверяем…" : needsTwoFactor ? "Подтвердить" : "Войти"}
          </button>
          {needsTwoFactor ? (
            <button
              className="button button--text"
              type="button"
              onClick={() => {
                setUseRecoveryCode((value) => !value);
                setCode("");
              }}
            >
              {useRecoveryCode
                ? "Использовать код аутентификатора"
                : "Использовать recovery-код"}
            </button>
          ) : null}
          {needsTwoFactor ? (
            <button
              className="button button--text"
              type="button"
              onClick={() => setNeedsTwoFactor(false)}
            >
              Вернуться к email и паролю
            </button>
          ) : null}
          {!needsTwoFactor ? (
            <Link className="button button--text" to="/forgot-password">
              Не помню пароль
            </Link>
          ) : null}
          <div className="login-install">
            <InstallButton />
          </div>
        </form>
      </section>
    </main>
  );
}
